import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  id: string;
  /** Whole days the component leaves stock before receipt. */
  value: number;
  onChange: (days: number) => void;
  /** Source lead time in days, used to scale the mini-timeline. */
  leadTimeDays: number;
}

/**
 * Presents Component.consume_offset as "Consumed N days before receipt"
 * with a tiny placement→receipt timeline marking where consumption falls.
 * Stores a single integer-day value; the meaning is made visible.
 */
export function ConsumeOffsetField({
  id,
  value,
  onChange,
  leadTimeDays,
}: Props) {
  const { t } = useTranslation();
  const span = Math.max(leadTimeDays, value, 1);
  // 0 = at receipt (right edge); larger offset moves the marker left toward placement.
  const markerPct = 100 - Math.min(100, (value / span) * 100);

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs">
        {t("sources.form.consumeOffsetLabel")}
      </Label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min={0}
          className="w-20"
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
        />
        <span className="text-muted-foreground text-xs">
          {t("sources.form.consumeOffsetSuffix")}
        </span>
      </div>
      <div
        className="bg-muted relative mt-1 h-1.5 rounded-full"
        aria-hidden="true"
      >
        <div
          className="bg-foreground absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full"
          style={{ left: `${markerPct}%` }}
        />
      </div>
      <div className="text-muted-foreground flex justify-between text-[10px]">
        <span>{t("sources.form.timelinePlace")}</span>
        <span>{t("sources.form.timelineReceipt")}</span>
      </div>
    </div>
  );
}
